import { ConfigurableChartContainer } from './ConfigurableChart.elements'
 
import { ReactNode, RefObject,  useEffect, useRef, useState  } from 'react'
import { generateDefaultAxisSettings, GenerateDefaultAxisSettingsProps, AxisSettingsProps } from './util/generateAxisSettings'
import { useContainerSize } from  'common/hooks/useContainerSize'
import { useConfiguredData } from './hooks/useConfiguredData'
import { HTMLBars } from './BarCharts/HTMLBars'
import { timerDelay } from 'common/helpers/timerDelay'
import { chart_types } from './chart_types'
import { useLocalStorageColors } from 'common/hooks/useLocalStorageColors'
import { ConfiguredChartAxis } from './components/ConfiguredChartAxis'
import { ConfiguredChartGridLines } from './components/ConfiguredChartGridLines'
import * as defaultHandlers from './util/defaultHandlers'
import { ConfiguredTooltip } from './components/ConfiguredTooltip'
 
import { ConfigurableLegend } from './components/ConfiguredLegend'
 
 
export type DataTypes = 'inline' | 'time-group' | 'summary' | 'grouped-summary'
 
export interface ConfigurableChartAxisProps {
  label?: ReactNode,
  ticks?: (string | number | Date)[],
  scale?: (value: string | number | Date) => number,
  scale_type?: 'linear' | 'time' | 'ordinal',
  format?: (value: string | number | Date) => string,
  [key: string]: unknown
}

export interface ConfigurableChartProps {
  version?: number,
  start_time?: string | number | Date,
  end_time?: string | number | Date,
  summary_key?: string,
  totals_key?: string,
  count_key?: string,
  data_type?: DataTypes,
  data?: unknown[],
  domain?: (string | number | Date)[],
  className?: string,
  demo?: boolean,
  [key: string]: unknown
}
  

export interface ConfiguredChartSettingsProps {
  // used to select all charts with the same group id to make sure the chart axis line up.
  chart_group?: string,
  color_keys?: string[],
  colors?: Record<string, string>,
  auto_colors?: boolean,
  disable_tooltip?: boolean,
  chart_kind?: string,
  vertical?: boolean,
  [key: string]: unknown  
}

export interface GeneratedChartSettingsProps extends GenerateDefaultAxisSettingsProps {
  chart_settings?: ConfiguredChartSettingsProps,
  [key: string]: unknown
}

const generateDefaultChartSettings = ({
  chart_settings = {} as  ConfiguredChartSettingsProps,
  group_key,
  timeZone,
  ...props
}: GeneratedChartSettingsProps): ConfiguredChartSettingsProps => {

  props = { ...props, time_zone: props?.time_zone || timeZone }
  
  const {
    axis_settings,
    color_key,
    data,
    colors,
    stacked
  } = (props || {}) as { axis_settings?: AxisSettingsProps, color_key?: string, data?: Record<string, unknown>[], checkIfFiltered?: (item: Record<string, unknown>) => boolean, checkIfSelected?: (item: Record<string, unknown>) => boolean, selected?: boolean }
 
  const {
    color_keys,
    auto_colors = true,
    chart_type = props?.chart_type as string | undefined,
    chart_kind = props?.chart_kind as string | undefined,
    disable_tooltip = (props?.disable_tooltip as boolean) || false,
    vertical = (props?.vertical as boolean) || false
  } = (chart_settings || {}) as ConfiguredChartSettingsProps

  if (!axis_settings) return chart_settings as ConfiguredChartSettingsProps
 
  if (auto_colors && data) {
    const color_ids = [] as string[]
    const color_keys = [] as string[] 
    if (color_key) color_keys.push(color_key)
    else {
      if (axis_settings?.left?.key) color_keys.push(axis_settings.left.key)
      if (axis_settings?.left?.keys) color_keys.push(...axis_settings.left.keys) 
      if (axis_settings?.right?.key) color_keys.push(axis_settings.right.key)
      if (axis_settings?.right?.keys) color_keys.push(...axis_settings.right.keys)
    }
 
    if (stacked) {
      group_key = group_key || axis_settings?.left?.keys && 'group'
    }
 
    for  (let i = 0; i < data?.length; i++) {
      const item = data?.[i]
      if (!item) continue
      color_keys?.forEach((key) => {
        const val = item?.[key]
        if (val && !color_ids.includes(String(val))) color_ids.push(String(val))
      })
    }
    const auto_colors = useLocalStorageColors(color_ids)
    chart_settings.colors = {...auto_colors, ...colors}
 
  }

  chart_settings = {
    group_key,
    color_keys,
    chart_type,
    chart_kind,
    vertical,
    disable_tooltip,
    ...(chart_settings || {}) 
  }
 
 
  return chart_settings as ConfiguredChartSettingsProps

}

export interface LegendItemProps {
  label?: ReactNode,
  color?: string,
  total?: ReactNode | number,
  average?: ReactNode | number,
  high?: ReactNode | number,
  low?: ReactNode | number,
  percent?: ReactNode | number,
  count?: ReactNode | number,
  [key: string]: unknown
}

export interface LegendSettingsProps {
  count_key?: string,
  label_key?: string,
  color_key?: string,
  totals_key?: string,
  data_type?: string,
  legend_items?: LegendItemProps[],
}

export interface ConfiguredChartLegendProps extends LegendSettingsProps {
  legend_items?: (LegendItemProps & unknown)[],
  colors?: Record<string, string>,
  mapKeys?: Record<string, string>,
  [key: string]: unknown
}

const generateLegendSettings = ({
  legend_settings = { } as LegendSettingsProps,
  ...props
}: ConfiguredChartLegendProps): LegendSettingsProps => {

  const {
    axis_settings,
    chart_settings 
  } = (props || {}) as { axis_settings?: AxisSettingsProps, chart_settings?: ConfiguredChartSettingsProps }
 
  const ns = {
    count_key: props?.count_key,
    label_key: props?.label_key,
    color_key: props?.color_key,
    totals_key: props?.totals_key,
    data_type: props?.data_type,
    ...(legend_settings || {})
  } as LegendSettingsProps    
  
  if (!ns?.label_key) {
    ns.label_key = axis_settings?.left?.key || axis_settings?.right?.key
  }

  ns.color_key = ns.color_key || ns.label_key || (chart_settings?.color_key as string) || 'color'

  return  ns as LegendSettingsProps
  
}

export const normalizeProps = ({ axis_settings: _axis_settings = {},  ...props}: Record<string, unknown>): Record<string, unknown> | undefined => {
  const axis_settings = (_axis_settings || {}) as AxisSettingsProps
  const {
 
     category_key,
 
    value_key,
    timestamp_key,
    value_keys,
    data_type,
     totals_key,
    summary = false,
  } = (props || {}) as Record<string, unknown>


  const {
    start_time,
    end_time
  } = props || {}

  if (!axis_settings?.left && !axis_settings?.right) {
    axis_settings.left = {
      key: value_key,
      keys: value_keys
    }
    if (summary && totals_key) {
      axis_settings.left = {
        ...axis_settings.left,
        key: value_key || 'total',
        keys: undefined,
        scale_type: 'linear'
      }
    }
  }

  if (!axis_settings?.bottom && !axis_settings?.top) {
    axis_settings.bottom = {
      key: timestamp_key || category_key
    }

    if (['time-groups'].includes((data_type || '') as string)) {
      axis_settings.bottom = {
        ...axis_settings.bottom,
        key: 'time_group',
        timestamp_key,
        scale_type: 'time'
      }
    }
  }
 
  return {axis_settings, start_time, end_time }

}

export const ConfigurableChart = ({
  version = 1.1,
  className, 
  domain,
  disable_tooltip = false,
  vertical = false,
  checkIfSelected,
  checkIfFiltered,
  handleClick,
  ...incoming_props
}: ConfigurableChartProps & Record<string, unknown>): JSX.Element => {

  const { chart_type } = incoming_props as Record<string, unknown> || {}

  if (version !== 1.1) {
    console.log('you are not using the current version', domain)
  }

  const internal_select_state = useState<Record<string, unknown> | undefined>(undefined)

  let setSelected = incoming_props?.setSelected as ((x: Record<string, unknown> | undefined) => void) | undefined
  let selected = incoming_props?.selected as Record<string, unknown> | undefined
  // use internal selector state if one is not provided in props
  if (!setSelected) [selected, setSelected] = internal_select_state
 

  // get the keys to use for selection based on the type of item being hovered (bar, legend item, etc)
  // move the default handlers to their own file
  checkIfSelected =  checkIfSelected || ((x?: Record<string, unknown>) => defaultHandlers.checkIfSelected(x, selected))
  checkIfFiltered = checkIfFiltered || ((x?: Record<string, unknown>) =>  defaultHandlers.checkIfFiltered(x, selected))
  handleClick = handleClick || ((x?: Record<string, unknown>) => defaultHandlers.handleClick(x, [selected, setSelected]))

  useEffect(() => {
   if (!incoming_props?.setSelected) setSelected?.(selected as Record<string, unknown>)
  }, [selected])
 
  const cached_data = [...(incoming_props?.data || [])] as unknown[]
  const { processed_data, data } = useConfiguredData(incoming_props?.data_type as unknown as Parameters<typeof useConfiguredData>[0], incoming_props) || {}
 
  const { containerRef: center_sizer_ref, ...sizer } = useContainerSize() as { containerRef: RefObject<HTMLDivElement>, width: number, height: number }
  const { containerRef: chart_ref, ...dims } = useContainerSize() as { containerRef: RefObject<HTMLDivElement>, width: number, height: number }
  
  const hover_ref = chart_ref//useRef(null) as RefObject<HTMLDivElement>
  const container_ref = useRef(null) as RefObject<HTMLDivElement>
  const tooltip_ref = useRef(null) as RefObject<HTMLDivElement>

  className = `configurable-chart${vertical ? ' vertical ' : ' '}configurable-${chart_type || 'unknown'}-chart ${className || ''}`.trim()
   
  let props = { chart_ref, container_ref, sizer, cached_data, tooltip_ref, hover_ref, ...incoming_props, disable_tooltip, vertical, selected, checkIfSelected, handleClick, checkIfFiltered,data, processed_data  } as Record<string, unknown>

  if (incoming_props?.chart_type === 'pie') {
    return  <>
      <ConfigurableLegend {...props} />
      <ConfigurableChartContainer data-init='false' data-sized='false' data-sizing='false' ref={container_ref} className={className}>
        <ConfiguredChart {...props} />
      </ConfigurableChartContainer>
    </>
  }

  // get axis_settings once to draw tick labels for dims
  let [axis_settings, ready_to_draw] = generateDefaultAxisSettings({...incoming_props, data, ...(dims ? dims : sizer) })
 
  useEffect(() => {
    const chart_el = chart_ref?.current as HTMLDivElement
    const container_el = container_ref?.current as HTMLDivElement
    if (!container_el || !chart_el) return

    container_el.dataset.sizing = 'true'
    
    // update axis_settings with width and height
    const [axs, draw] =  generateDefaultAxisSettings({...incoming_props, data, ...dims }) || []
    
    axis_settings = axs
    ready_to_draw = draw

    timerDelay(300, () => {
      container_el.dataset.sizing = 'false'
      container_el.dataset.sized = 'true'
      container_el.dataset.init = 'true'
    })

  }, [dims, container_ref, axis_settings, chart_ref ])  

  if (!ready_to_draw || !axis_settings) return <ConfigurableChartContainer ref={container_ref} className={className}>loading</ConfigurableChartContainer>
  props = { ...props, axis_settings, container_ref, chart_ref, hover_ref, tooltip_ref }
  
  // this returns formatted dand colors
  const chart_settings = generateDefaultChartSettings({ ...props, axis_settings, data, checkIfFiltered, checkIfSelected, selected, ...dims })
  props = { ...props, ...chart_settings, ...dims }
 
  if (props?.chart_type) className += ` ${props?.chart_type}-chart`
  const legend_settings = generateLegendSettings(props)
  props = { ...props, legend_settings }

  const { sizes: sizes  } = getDims(
    container_ref?.current as HTMLDivElement,
    { sizer, ready_to_size: sizer?.width > 0 && sizer?.height > 0 }
  ) || {}
 
  const inner_grid_style = { 
    gridTemplateColumns: `${sizes?.left || 0}px 1fr ${sizes?.right || 0}px`, 
    gridTemplateRows: `auto 1fr ${sizes?.bottom || 0}px`
  }

  const container_settings = {
    style: {
      gridTemplateColumns: `auto ${inner_grid_style.gridTemplateColumns} auto`,
      gridTemplateRows: `auto ${inner_grid_style.gridTemplateRows} auto`
    },
    ref: container_ref,
    className,
    'data-id': 'configurable-chart-container'
  }
 
  const scroll_area_styles = {  
    gridTemplateColumns: `${sizes?.left || 0}px ${sizer?.width || 0}px ${sizes?.right || 0}px`,
    gridTemplateRows: `${sizes?.top || 0}px ${sizer?.height || 0}px ${sizes?.bottom || 0}px`,
  }
 
  disable_tooltip = disable_tooltip || chart_settings?.disable_tooltip || chart_settings?.chart_type === 'bar'  || props?.chart_type === 'bar' ? true : false 
  
  return  <>
    <ConfigurableLegend {...props} />
    <ConfigurableChartContainer {...container_settings} data-init='false' data-sized='false' data-sizing='false' ref={container_ref}>

      
      <div data-area='scroll-area' data-id='scroll-area' className='scroll-area inner-grid loading-grid' style={scroll_area_styles}>
        <div data-id='scroll-area-inner' className='scroll-mask inner-grid loading-grid' style={inner_grid_style}>

          <ConfiguredChartAxis  loc='top' {...axis_settings?.top} {...{  auto_style: true }} />
          <ConfiguredChartAxis  loc='bottom' {...axis_settings?.bottom} {...{  auto_style: true }} />
          
          { 
          // only load chart after we have all the sizes
          }
          <div data-area='center' className='grid-lines'>
            <ConfiguredChartGridLines {...props} />
          </div>          

          <ConfiguredChart {...props} />
                       
        </div> 
      </div>
      <div className='scroll-shader' data-axis='right' data-id='shader' />
      <div className='scroll-shader' data-axis='left' data-id='shader'/>
      <ConfiguredChartAxis  loc='left' {...axis_settings?.left} {...{  auto_style: true }} />
      <ConfiguredChartAxis  loc='right' {...axis_settings?.right} {...{  auto_style: true }} />   
        
      {
      // this is where the chart is getting it's width and height from.
      }  
      <div ref={center_sizer_ref} className='center-sizer' data-area='center' />
      
      {
      // axis labels - these are separate from the axis to allow them to be sized based on the tick labels without affecting the axis line/ticks
      }
      <div data-area='label-top' data-axis='top' data-id='label' className='label label--top'><div className='inner'>{axis_settings?.top?.label || axis_settings?.top?.key}</div></div>
      <div data-area='label-bottom' data-axis='bottom' data-id='label' className='label label--bottom'>
        <div className='inner'>
          {axis_settings?.bottom?.label || axis_settings?.bottom?.key}
         {!!(props?.start_time) && !!(props?.end_time) &&  <div>{new Date(props.start_time as string | number | Date).toISOString()} - {new Date(props.end_time as string | number | Date).toISOString()}</div>}
          </div>
      </div>
      <div data-area='label-left' data-axis='left' data-id='label' className='label label--left'><div className='inner'>{axis_settings?.left?.label || axis_settings?.left?.key}</div></div>
      <div data-area='label-right' data-axis='right' data-id='label' className='label label--right'><div className='inner'>{axis_settings?.right?.label || axis_settings?.right?.key}</div></div>
 
      {
      // chart sizers - theres are need to sized the chart.  the chart is sized based on the axis dimensions
      }
      <div style={{width: `${sizes?.left || 0}px`, height: `${sizes?.top || 0}px`}} data-axis='top-left' data-area='tl' data-tb='t' data-lr='l' data-id='spacer' className='grid-spacer'/>
      <div style={{width: `${sizes?.right || 0}px`, height: `${sizes?.top || 0}px`}} data-axis='top-right' data-area='tr' data-tb='t' data-lr='r' data-id='spacer' className='grid-spacer'/>
      <div style={{width: `${sizes?.left || 0}px`, height: `${sizes?.bottom || 0}px`}} data-axis='bottom-left' data-area='bl' data-tb='b' data-lr='l' data-id='spacer' className='grid-spacer'/>
      <div style={{width: `${sizes?.right || 0}px`, height: `${sizes?.bottom || 0}px`}} data-axis='bottom-right' data-area='br' data-tb='b' data-lr='r' data-id='spacer' className='grid-spacer'/>      
      
    </ConfigurableChartContainer>
  </>
} 



const ConfiguredChart = (props: Record<string, unknown>) => {
 
  const {
    chart_type, 
    tooltip_ref,
    chart_ref
 } = (props || {}) as Record<string, unknown>
  
  const Chart = chart_types?.[chart_type as keyof typeof chart_types] || (() => <div  >chart type {chart_type} not found</div>)

  const toggleTooltip = (show = false ) => {
    const tooltip_el = tooltip_ref?.current as HTMLDivElement
    if (!tooltip_el) return
    tooltip_el.classList.toggle('is-hidden', !show)
    tooltip_el.classList.toggle('is-visible', show)
    if (!show) { 
      delete tooltip_el.dataset.xval
      delete tooltip_el.dataset.yval
    }
  }

  const handleMouseMove = (e: MouseEvent) => {
    
    const tooltip_el = tooltip_ref?.current as HTMLDivElement | null
 
    if (!tooltip_el) return
 
    const group = e?.currentTarget?.dataset?.group
    const data_index = e?.currentTarget?.dataset?.index

    const rect = (e.currentTarget as HTMLDivElement)?.getBoundingClientRect()
    const xpos = Math.max(0, e.clientX - (rect?.left ?? 0))
    const ypos = Math.max(0, e.clientY - (rect?.top ?? 0))

    const xval = e?.currentTarget?.dataset?.xval
    const yval = e?.currentTarget?.dataset?.yval
 
    tooltip_el.dataset.xval = xval || ''
    tooltip_el.dataset.yval = yval || ''
    
    tooltip_el.dataset.xpos = String(xpos)
    tooltip_el.dataset.ypos = String(ypos)
    
    if (group) tooltip_el.dataset.group = group
    else delete tooltip_el.dataset.group

    if (data_index) tooltip_el.dataset.index = String(data_index)
    else delete tooltip_el.dataset.index
    
  }

  props = { ...props, toggleTooltip   }

  return ( 
    <div onMouseMove={chart_type !== 'bar' && handleMouseMove} data-area='center' className='chart-sizer grid tooltip-area rel' ref={chart_ref}  onMouseEnter={(e) => toggleTooltip?.(true, e)} onMouseLeave={(e) => toggleTooltip?.(false, e)}>
      {chart_type === 'bar' 
        ? <HTMLBars {...props} />
        : <>
          <Chart {...props}/> 
          <ConfiguredChartTooltip {...props} />
          </>}
    </div>
  )
}

const ConfiguredChartTooltip = ( props?: { position?: string, [key: string]: unknown }): JSX.Element => {
let position = props?.position || 'top'
const {
  data,
  axis_settings,
  chart_type,
  colors
} = (props || {}) as { position?: string, [key: string]: unknown }
 
const [tooltip, setTooltip] = useState<Record<string, unknown>>({ })

const xpos = Number(tooltip?.xpos)
const ypos = Number(tooltip?.ypos)

const x_scaler = axis_settings?.bottom || axis_settings?.top
const y_scaler = axis_settings?.left || axis_settings?.right

let xval = x_scaler?.scale?.invert?.(xpos)
let yval = y_scaler?.scale?.invert?.(ypos)

const keys = y_scaler?.keys 

if (y_scaler?.scale_type === 'time') {
  yval = new Date(yval as number).getTime()
  if (chart_type === 'line') position = 'side'
}

if (x_scaler?.scale_type === 'time') {
  xval = new Date(xval as number).getTime()
  if (chart_type === 'line') position = 'side'
}
 
let closest_point = null

if (data && xval !== undefined && (keys || yval !== undefined)) {
  let closest_distance = Infinity
  for (let i = 0; i < data?.length; i++) {
    const item = data?.[i] as Record<string, unknown>
    if (!item) continue
    let item_xval = item?.[x_scaler?.key as string]
    if (x_scaler?.scale_type === 'time') item_xval = new Date(item_xval as number).getTime()
    if (item_xval === undefined) continue
    const x_distance = Math.abs((item_xval as number) - (xval as number))
    let distance = x_distance
    if (!keys) {
      let item_yval = item?.[y_scaler?.key as string]
      if (y_scaler?.scale_type === 'time') item_yval = new Date(item_yval as number).getTime()
      if (item_yval === undefined) continue
      const y_distance = Math.abs((item_yval as number) - (yval as number))
      distance = Math.sqrt(x_distance ** 2 + y_distance ** 2)
    } 

    if (distance < closest_distance) {
      closest_distance = distance
      closest_point = item
    }
  }
}
 
let x = `${xpos}px`
let y = `${ypos}px`
if (closest_point) {
  x = `${x_scaler?.scale?.(closest_point?.[x_scaler?.key as string] as number || 0) || xpos}px`
  y = `${y_scaler?.scale?.(closest_point?.[y_scaler?.key as string] as number || 0) || ypos}px`
}

let point_x = closest_point?.[x_scaler?.key as string]
let point_y = closest_point?.[y_scaler?.key as string]
let closest_y_val = point_y

if (closest_point && keys) {
  let closest_y_distance = Infinity
  keys.forEach((key: string) => {
    let item_yval = closest_point?.[key as string]
    if (y_scaler?.scale_type === 'time') item_yval = new Date(item_yval as number).getTime()
    if (item_yval === undefined) return

    const y_distance = Math.abs((item_yval as number) - (yval as number))
    if (y_distance < closest_y_distance) {
      closest_y_distance = y_distance
      closest_y_val = item_yval
    }
  })

  point_y = closest_y_val
}

if (y_scaler?.scale_type === 'time') point_y = new Date(point_y as number).getTime()
if (x_scaler?.scale_type === 'time') point_x = new Date(point_x as number).getTime()

//console.log({closest_point, xval, yval})
let point_styles = {
  width: '.3em',
  height: '.3em',
  borderRadius: '50%',
  transform: 'translate(-50%, -50%)',
  position: 'absolute',
  left: x,
  top: y,
}
 
if (!closest_point) point_styles = { display: 'none' }

const new_tooltip = { 
  ...tooltip,
  item: closest_point,
  x_val: closest_point?.[x_scaler?.key as string] || '',
  y_val: closest_y_val as string || '',
  title: closest_point?.[x_scaler?.key as string] || '',
  value: closest_point?.[y_scaler?.key as string] || ''
}
 
const tooltip_settings = {
  ...props,
  position,
  left: `${point_x ? x_scaler?.scale?.(point_x as number || 0) : xpos}px`,
  top: `${point_y ? y_scaler?.scale?.(point_y as number || 0) : ypos}px`,
  ...tooltip,
  tooltip: new_tooltip,
  setTooltip
}

let DataPoint = <></>

if (!keys && closest_point) {
  DataPoint = 
    <div className='data-point' style={{
      ...point_styles,
      background: colors?.[y_scaler?.key as string] || 'green'
    }} />
} else if (keys && closest_point) {
  keys.forEach((key) => {

     DataPoint = <>
      <div className='data-point' key={key} style={{
        ...point_styles,
        top: `${y_scaler?.scale?.(closest_point?.[key as string] as number || 0)}px`,
        background:  colors?.[key as string] || 'green'
      }} />
      {DataPoint}
    </>

   })
  
}
 
return  <>
  {DataPoint}
  {keys && closest_point && keys.map((key) => {
    return (
      <div className='data-point' key={key} style={{
        ...point_styles,
        top: `${y_scaler?.scale?.(closest_point?.[key as string] as number || 0)}px`,
        background:  colors?.[key as string] || 'green'
      }} />
    )
  })}
  <ConfiguredTooltip {...tooltip_settings} />
  </>
}
          
 


 
const getDims = (
  el?: HTMLDivElement, 
  props?: Record<string, unknown >) => {

  const {
    sizer,
    round = false,
    sizer_selector = '[data-id="tick-label"]',
    ready_to_size = false,
  } = (props || {}) as Record<string, unknown>
 
  let ready_for_size_check = ready_to_size
  let sized = false

  if (!el) return { ready_for_size_check}

  let sizes = {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: 0,
    height: 0
  }

  const chart_max_dims = {} as Record<string, { width: number, height: number }>
  const chart_totals = {} as Record<string, { width: number, height: number }>
  const overlapping = {} as Record<string, boolean>
   
  el?.querySelectorAll?.(sizer_selector as string)?.forEach((l_el) => {
    const label_el = l_el as HTMLDivElement
    if (!label_el) return
    const loc = label_el?.dataset?.axis
    if (!loc) return
    if (!chart_max_dims?.[loc]) chart_max_dims[loc] = { width: 0, height: 0 }
    if (!chart_totals?.[loc]) chart_totals[loc] = { width: 0, height: 0 }

    const label_rect = label_el.getBoundingClientRect()
    
    chart_totals[loc].width += round ? Math.ceil(label_rect.width) : label_rect.width
    chart_totals[loc].height += round ? Math.ceil(label_rect.height) : label_rect.height
 
    if (label_rect.width > chart_max_dims[loc].width) chart_max_dims[loc].width = round ? Math.ceil(label_rect.width) : label_rect.width    
    if (label_rect.height > chart_max_dims[loc].height) chart_max_dims[loc].height =  round ? Math.ceil(label_rect.height) : label_rect.height
    
    ready_for_size_check = true

     if (['top', 'bottom'].includes(loc)) {
      if (chart_totals?.[loc]?.width > (sizer?.width || 0)) overlapping[loc] = true
      else overlapping[loc] = false
    } else {
      if (chart_totals?.[loc]?.height > (sizer?.height || 0)) overlapping[loc] = true
      else overlapping[loc] = false
    }
    
  })
  
  if (sizer) {

    sizes = {
      left: chart_max_dims?.left?.[!overlapping?.left ? `width` : `height`] || 0,
      right: (chart_max_dims?.right?.[!overlapping?.right ? `width` : `height`] || 0),
      top: chart_max_dims?.top?.[!overlapping?.top ? `height` : `width`] || 0,
      bottom: (chart_max_dims?.bottom?.height),
      width: 0,
      height: 0
    }

    sizes.width = (sizer?.width || 0) - sizes.left - sizes.right
    sizes.height = (sizer?.height || 0) - sizes.top - sizes.bottom 

    if (round) {
      sizes.width = Math.ceil(sizes.width)
      sizes.height = Math.ceil(sizes.height)
    }

    sized = true
  }

  Object.entries(overlapping).forEach(([loc, is_overlapping]) => {
    sized = false
    if (is_overlapping) {
      const axis_el = el?.querySelector(`[data-id="axis"][data-axis="${loc}"]`) as HTMLDivElement | null
      if (!axis_el) return
      if (axis_el) axis_el.classList.add('has-overlapping-labels')
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      else axis_el.classList.remove('has-overlapping-labels')
    }
    sized = true
  })
 
  return { chart_max_dims, chart_totals, sizes, overlapping, ready_for_size_check, sized  }

}
 